import { useRef, useState, type DragEvent, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface DropzoneProps {
  onFile: (file: File) => void
  accept: string
  /** Shown under the prompt: formats, size ceiling. */
  hint?: ReactNode
  disabled?: boolean
  busy?: boolean
  className?: string
}

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden="true">
    <path
      d="M12 15.5V4M8 7.5L12 3.5l4 4M4 16v2.5A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5V16"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * The upload target.
 *
 * A real drop zone rather than a bare `<input type="file">` wearing a link:
 * it is a labelled button for the keyboard, it accepts a drag from the
 * desktop, and it says out loud what it will take before you try. The native
 * input stays in the DOM — hidden, not replaced — so the file picker, form
 * semantics and assistive tech all keep working.
 */
export function Dropzone({
  onFile,
  accept,
  hint,
  disabled,
  busy,
  className,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    if (disabled) return
    const dropped = event.dataTransfer.files?.[0]
    if (dropped) onFile(dropped)
  }

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={cn(
        'relative rounded-lg border border-dashed transition-colors duration-150 ease-out',
        dragging
          ? 'border-accent bg-accent-soft'
          : 'border-line-strong bg-surface-sunken hover:border-accent-ring hover:bg-surface-hover',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        // Visually hidden but still reachable in a screen reader's forms mode,
        // where it would otherwise be announced as an unnamed "file upload".
        aria-label="Choose a file to import"
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          const chosen = event.target.files?.[0]
          if (chosen) onFile(chosen)
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="flex w-full flex-col items-center gap-2 px-4 py-8 text-center"
      >
        <span
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-full transition-colors',
            dragging ? 'bg-accent text-white' : 'bg-surface-raised text-ink-muted shadow-xs',
            busy && 'animate-pulse',
          )}
        >
          <UploadIcon />
        </span>
        <span className="text-base text-ink">
          <span className="font-medium text-accent-text">Choose a file</span>
          <span className="text-ink-secondary"> or drag one here</span>
        </span>
        {hint ? <span className="text-xs text-ink-muted">{hint}</span> : null}
      </button>
    </div>
  )
}
